<div class="my-8 mx-auto flex flex-col gap-4">
  <!-- Header -->
  <div class="flex justify-between items-center w-full">
    <app-btn-back routeTo=".." icon="fa fa-chevron-left"></app-btn-back>
  </div>

  <div>
    <h1 class="text-2xl font-semibold text-primary-500 tracking-tight">Upload Expense Receipts</h1>
    <p class="text-sm text-blueGray-500 mt-1">
      Upload payment receipts in PDF format to record them as expenses in the ledger.
    </p>
  </div>

  <!-- Group Settings Info (AI Status) -->
  @if (!isAiEnabled) {
  <div class="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-amber-800">
    <i class="fa fa-circle-exclamation text-xl text-amber-500 mt-0.5"></i>
    <div>
      <h4 class="font-bold text-sm">AI receipt parsing is disabled</h4>
      <p class="text-xs text-amber-700 mt-0.5 leading-relaxed">
        AI parsing is currently disabled in your Group Settings. You can still upload receipts and enter their
        transaction details manually below.
      </p>
    </div>
  </div>
  }

  <!-- PDF File Dropzone -->
  <div
    class="border-2 border-dashed border-blueGray-200 hover:border-primary-400 bg-blueGray-50 hover:bg-primary-50 hover:bg-opacity-30 rounded-2xl p-8 text-center transition-all cursor-pointer relative flex flex-col items-center justify-center min-h-[160px]">
    <input type="file" (change)="onFileSelected($event)" accept="application/pdf" multiple
      class="absolute inset-0 opacity-0 cursor-pointer" />
    <div class="flex flex-col items-center gap-2">
      <i class="fa-solid fa-file-pdf text-4xl text-primary-500 mb-1"></i>
      <span class="text-sm font-semibold text-blueGray-700">Drag & drop receipt PDFs here, or click to browse</span>
      <span class="text-xs text-blueGray-400">Upload up to 10 PDF files (Max 5MB per file)</span>
    </div>
  </div>

  <!-- Uploaded Receipts List -->
  @if (receipts.length > 0) {
  <div class="flex flex-col gap-6">
    <div class="flex justify-between items-center border-b border-blueGray-100 pb-2">
      <h3 class="font-bold text-blueGray-800 text-lg">Receipts Queue ({{ receipts.length }} / 10)</h3>
    </div>

    @for (item of receipts; track item.id) {
    <div
      class="bg-white rounded-2xl p-5 md:p-6 shadow-sm border border-blueGray-100 flex flex-col gap-4 relative overflow-hidden transition-all"
      [ngClass]="item.isSaved ? 'opacity-75 border-emerald-200 bg-emerald-50 bg-opacity-10' : ''">

      <!-- Card Header -->
      <div class="flex justify-between items-center border-b border-blueGray-50 pb-3">
        <div class="flex items-center gap-2.5">
          <i class="fa-solid fa-file-pdf text-2xl text-red-500"></i>
          <div>
            <h4 class="font-bold text-blueGray-800 text-sm truncate max-w-md">{{ item.fileName }}</h4>
            <span class="text-[10px] text-blueGray-400 uppercase font-mono">PDF Document</span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          @if (isAiEnabled && !item.isSaved) {
          <button (click)="scanReceipt(item)" [disabled]="item.isScanning || item.isSaving"
            class="text-indigo-600 hover:text-indigo-800 font-bold text-xs py-1.5 px-3 rounded-lg flex items-center gap-1.5 transition-all focus:outline-none">
            <i class="fa" [ngClass]="item.isScanning ? 'fa-spinner fa-spin' : 'fa-magic'"></i>
            {{ item.isScanning ? 'Scanning...' : 'Scan with AI' }}
          </button>
          }
          <button (click)="removeFile(item)" [disabled]="item.isScanning || item.isSaving" class="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
            title="Remove receipt">
            <i class="fa fa-trash-can"></i>
          </button>
        </div>
      </div>

      <!-- Error Message -->
      @if (item.error) {
      <div class="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-xl flex gap-2">
        <i class="fa fa-circle-info mt-0.5"></i>
        <span>{{ item.error }}</span>
      </div>
      }

      <!-- Form details -->
      <form [formGroup]="item.form" (ngSubmit)="saveExpense(item)" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Amount & Currency -->
        <div class="flex gap-2">
          <div class="w-1/3">
            <label class="block text-[10px] font-bold uppercase tracking-wider text-blueGray-400 mb-1">Currency</label>
            <select formControlName="currency" [attr.disabled]="(item.isSaved || item.isScanning || item.isSaving) ? true : null"
              class="w-full rounded-lg border border-blueGray-200 bg-blueGray-50 p-2.5 text-xs text-blueGray-700 focus:border-primary-500 focus:outline-none">
              <option value="usd">USD</option>
              <option value="eur">EUR</option>
              <option value="gbp">GBP</option>
            </select>
          </div>
          <div class="w-2/3">
            <label class="block text-[10px] font-bold uppercase tracking-wider text-blueGray-400 mb-1">Amount</label>
            <input type="number" formControlName="amount" placeholder="0.00" [readonly]="item.isSaved || item.isScanning || item.isSaving"
              class="w-full py-2.5 px-3 rounded-lg border border-blueGray-200 bg-blueGray-50 text-xs text-blueGray-700 focus:border-primary-500 focus:outline-none" />
          </div>
        </div>

        <!-- Conversion Rate -->
        @if (item.form.get('currency')?.value !== (group?.currency || 'USD').toLowerCase()) {
        <div class="col-span-1 md:col-span-2 animate-in fade-in slide-in-from-top-1 duration-200">
          <label class="block text-[10px] font-bold uppercase tracking-wider text-blueGray-400 mb-1">Conversion Rate *</label>
          <div class="flex gap-2 items-center">
            <span class="text-xs text-blueGray-500">1 {{ item.form.get('currency')?.value | uppercase }} =</span>
            <input type="number" step="0.00001" formControlName="conversionRate" placeholder="1.0" [readonly]="item.isSaved || item.isScanning || item.isSaving"
              class="w-full py-2.5 px-3 rounded-lg border border-blueGray-200 bg-blueGray-50 text-xs text-blueGray-700 focus:border-primary-500 focus:outline-none" />
            <span class="text-xs text-blueGray-500">{{ (group?.currency || 'USD') | uppercase }}</span>
          </div>
        </div>
        }

        <!-- Date -->
        <div>
          <label class="block text-[10px] font-bold uppercase tracking-wider text-blueGray-400 mb-1">Date</label>
          <input type="date" formControlName="date" [readonly]="item.isSaved || item.isScanning || item.isSaving"
            class="w-full py-2.5 px-3 rounded-lg border border-blueGray-200 bg-blueGray-50 text-xs text-blueGray-700 focus:border-primary-500 focus:outline-none" />
        </div>

        <!-- Paid From (Source/Payer) -->
        <div>
          <label class="block text-[10px] font-bold uppercase tracking-wider text-blueGray-400 mb-1">Paid From
            (Payer/Source)</label>
          <input type="text" formControlName="from" placeholder="e.g. Group Treasurer, Main Account"
            [readonly]="item.isSaved || item.isScanning || item.isSaving"
            class="w-full py-2.5 px-3 rounded-lg border border-blueGray-200 bg-blueGray-50 text-xs text-blueGray-700 focus:border-primary-500 focus:outline-none" />
        </div>

        <!-- Paid To (Merchant/Vendor) -->
        <div>
          <label class="block text-[10px] font-bold uppercase tracking-wider text-blueGray-400 mb-1">Paid To
            (Merchant/Vendor) *</label>
          <input type="text" formControlName="to" placeholder="e.g. Office Depot, Amazon" [readonly]="item.isSaved || item.isScanning || item.isSaving"
            class="w-full py-2.5 px-3 rounded-lg border border-blueGray-200 bg-blueGray-50 text-xs text-blueGray-700 focus:border-primary-500 focus:outline-none" />
        </div>

        <!-- Description -->
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold uppercase tracking-wider text-blueGray-400 mb-1">Description
            *</label>
          <input type="text" formControlName="description" placeholder="What was purchased?" [readonly]="item.isSaved || item.isScanning || item.isSaving"
            class="w-full py-2.5 px-3 rounded-lg border border-blueGray-200 bg-blueGray-50 text-xs text-blueGray-700 focus:border-primary-500 focus:outline-none" />
        </div>

        <!-- Tags -->
        <div class="md:col-span-2">
          <label class="block text-[10px] font-bold uppercase tracking-wider text-blueGray-400 mb-1">Tags
            (comma-separated)</label>
          <input type="text" formControlName="tags" placeholder="e.g. supplies, office, travel"
            [readonly]="item.isSaved || item.isScanning || item.isSaving"
            class="w-full py-2.5 px-3 rounded-lg border border-blueGray-200 bg-blueGray-50 text-xs text-blueGray-700 focus:border-primary-500 focus:outline-none" />
        </div>

        <!-- Save button -->
        <div class="md:col-span-2 flex justify-end mt-2">
          @if (item.isSaved) {
          <span class="inline-flex items-center gap-1.5 text-emerald-600 font-bold text-xs">
            <i class="fa-solid fa-circle-check text-base"></i> Recorded in Ledger
          </span>
          } @else {
          <button type="submit" [disabled]="(item.form.invalid && !item.isScanning && !item.isSaving) || item.isScanning || item.isSaving"
            class="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-bold text-xs py-2.5 px-5 rounded-lg flex items-center gap-1.5 transition-all shadow-md">
            @if (item.isSaving) {
            <i class="fa fa-spinner fa-spin"></i> Saving...
            } @else if (item.isScanning) {
            <i class="fa fa-spinner fa-spin"></i> Scanning with AI...
            } @else {
            <i class="fa fa-cloud-arrow-up"></i> Save Expense
            }
          </button>
          }
        </div>
      </form>

    </div>
    }
  </div>
  } @else {
  <div
    class="bg-white rounded-2xl p-10 border border-blueGray-100 shadow-sm text-center flex flex-col items-center justify-center py-20 text-blueGray-400">
    <i class="fa-solid fa-receipt text-5xl mb-4 text-blueGray-300"></i>
    <h3 class="font-bold text-blueGray-700 text-base mb-1">No receipts uploaded</h3>
    <p class="text-xs text-blueGray-400 max-w-sm leading-relaxed">
      Select or drop one or multiple PDF receipts above to start recording your expenses.
    </p>
  </div>
  }

</div>